import { useRef, useState } from "react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { ReactSVG } from "react-svg";

import lager from "../assets/img/lager.png";
import nonFiltrata from "../assets/img/non-filtrata.png";
import star from "../assets/svg/star.svg";
import arrow from "../assets/svg/arrow.svg";
import { useMediaQuery } from "@uidotdev/usehooks";

gsap.registerPlugin(useGSAP);

const Products = () => {
	const containerRef = useRef();

	const lagerRef = useRef();
	const lagerContentRef = useRef();
	const lagerStarRef = useRef();
	const lagerArrowRef = useRef();

	const unfilteredRef = useRef();
	const unfilteredContentRef = useRef();
	const unfilteredStarRef = useRef();
	const unfilteredArrowRef = useRef();

	const isMobile = useMediaQuery("only screen and (max-width : 767px)");

	const [isLagerOpen, setIsLagerOpen] = useState(false);
	const [isUnfilteredOpen, setIsUnfilteredOpen] = useState(false);

	const { contextSafe } = useGSAP({ scope: containerRef });

	const openLager = contextSafe(() => {
		if (!isLagerOpen && !isMobile) {
			lagerRef.current.style.zIndex = 10;

			const tl = gsap.timeline();

			tl.to(lagerRef.current, {
				width: "100vw",
			})
				.to(
					lagerContentRef.current,
					{
						translateX: 0,
					},
					"<"
				)
				.to(
					lagerStarRef.current,
					{
						translateX: 0,
					},
					"<"
				)
				.to(
					lagerArrowRef.current,
					{
						rotate: 180,
					},
					"<"
				);

			setIsLagerOpen(true);
		}
	});

	const closeLager = contextSafe(() => {
		if (isLagerOpen) {
			const tl = gsap.timeline();

			tl.to(lagerRef.current, {
				width: "50vw",
			})
				.to(
					lagerContentRef.current,
					{
						translateX: "100%",
					},
					"<"
				)
				.to(
					lagerStarRef.current,
					{
						translateX: "100%",
					},
					"<"
				)
				.to(
					lagerArrowRef.current,
					{
						rotate: 0,
					},
					"<"
				)
				.add(() => {
					lagerRef.current.style.zIndex = 1;
				});

			setIsLagerOpen(false);
		}
	});

	const openUnfiltered = contextSafe(() => {
		if (!isUnfilteredOpen && !isMobile) {
			unfilteredRef.current.style.zIndex = 10;

			const tl = gsap.timeline();

			tl.to(unfilteredRef.current, { width: "100vw" })
				.to(
					unfilteredContentRef.current,
					{
						translateX: 0,
					},
					"<"
				)
				.to(
					unfilteredStarRef.current,
					{
						translateX: 0,
					},
					"<"
				)
				.to(
					unfilteredArrowRef.current,
					{
						rotate: -180,
					},
					"<"
				);

			setIsUnfilteredOpen(true);
		}
	});

	const closeUnfiltered = contextSafe(() => {
		if (isUnfilteredOpen) {
			const tl = gsap.timeline();

			tl.to(unfilteredRef.current, {
				width: "50vw",
			})
				.to(
					unfilteredContentRef.current,
					{
						translateX: "-100%",
					},
					"<"
				)
				.to(
					unfilteredStarRef.current,
					{
						translateX: "-100%",
					},
					"<"
				)
				.to(
					unfilteredArrowRef.current,
					{
						rotate: 0,
					},
					"<"
				)
				.add(() => {
					unfilteredRef.current.style.zIndex = 1;
				});

			setIsUnfilteredOpen(false);
		}
	});

	return (
		<>
			<section className="relative w-full md:h-svh" ref={containerRef}>
				<div
					className={`bg-light-blue top-0 left-0 flex h-full w-[100vw] flex-col items-start justify-center overflow-hidden px-6 py-12 md:absolute md:w-[50vw] md:px-0 md:py-0 ${!isLagerOpen && "md:cursor-pointer"}`}
					onClick={openLager}
					ref={lagerRef}>
					<div className="relative flex w-full items-center justify-center">
						<h2 className="text-center font-serif text-5xl text-white uppercase lg:text-6xl xl:text-7xl">
							Fùrlan Lager
						</h2>
						<div className="absolute right-0 hidden px-5 md:block xl:px-10" ref={lagerArrowRef} onClick={closeLager}>
							<ReactSVG src={arrow} className="-scale-x-100 cursor-pointer [&_svg]:h-auto [&_svg]:w-7" />
						</div>
					</div>
					<div className="relative flex w-full flex-col pb-5 md:w-[100vw] md:flex-row md:pb-0">
						<div className="flex items-center justify-center md:w-[50vw]">
							<img src={lager} alt="" className="h-auto w-5/6 select-none md:w-1/2" draggable="false" />
						</div>
						<div
							className="z-10 flex flex-col items-start justify-center gap-6 text-lg text-white md:w-[50vw] md:translate-x-full md:pr-8 lg:text-xl"
							ref={lagerContentRef}>
							<p className="max-w-sm">
								Un aroma <strong>pulito</strong> e <strong>armonioso</strong>, con <strong>leggere note tostate</strong>
								.<br />
								Il luppolo emerge con <strong>discrezione</strong>, aggiungendo una <strong>freschezza amara</strong>{" "}
								che non sovrasta mai.
							</p>
							<p className="pr-30 md:pr-0">
								<strong>È una birra essenziale, fatta&nbsp;con&nbsp;carattere.</strong>
							</p>
						</div>
						<div
							className="absolute -right-27 -bottom-37 w-65 md:-right-35 md:-bottom-88 md:w-115 md:max-w-1/2 md:translate-x-full xl:-right-8 xl:-bottom-55"
							ref={lagerStarRef}>
							<ReactSVG
								src={star}
								className="[&_svg]:fill-blue [&_svg]:h-auto [&_svg]:w-full [&_svg]:rotate-12 [&_svg]:md:-rotate-6"
							/>
							<p className="absolute top-[43%] left-1/3 font-serif text-lg text-white md:text-2xl lg:text-3xl">
								VOL 4.8%
							</p>
						</div>
					</div>
				</div>
				<div
					className={`bg-yellow top-0 right-0 flex h-full w-[100vw] flex-col items-end justify-center overflow-hidden px-6 py-12 md:absolute md:w-[50vw] md:px-0 md:py-0 ${!isUnfilteredOpen && "md:cursor-pointer"}`}
					onClick={openUnfiltered}
					ref={unfilteredRef}>
					<div className="relative flex w-full items-center justify-center">
						<h2 className="text-center font-serif text-5xl text-white uppercase lg:text-6xl xl:text-7xl">
							Non Filtrata
						</h2>
						<div
							className="absolute left-0 hidden px-5 md:block xl:px-10"
							ref={unfilteredArrowRef}
							onClick={closeUnfiltered}>
							<ReactSVG src={arrow} className="cursor-pointer [&_svg]:h-auto [&_svg]:w-7" />
						</div>
					</div>
					<div className="relative flex w-full flex-col-reverse pb-5 md:w-[100vw] md:flex-row md:pb-0">
						<div
							className="z-10 flex flex-col items-start justify-center gap-6 text-lg text-white md:w-[50vw] md:-translate-x-100 md:items-end md:pl-8 lg:text-xl"
							ref={unfilteredContentRef}>
							<p className="max-w-sm">
								Un fresco <strong>bouquet floreale</strong>, con note <strong>tropicali</strong>, di{" "}
								<strong>albicocca</strong> e di <strong>frutti rossi</strong> che si intrecciano con il luppolo
								aromatico utilizzato in <strong>dry hopping</strong>, regalando equilibrio e un tocco autentico.
							</p>
							<p className="w-full max-w-sm pr-30 md:pr-0">
								<strong>È la birra, fatta&nbsp;come piace&nbsp;a&nbsp;noi.</strong>
							</p>
						</div>
						<div className="flex items-center justify-center md:w-[50vw]">
							<img src={nonFiltrata} alt="" className="h-auto w-5/6 select-none md:w-1/2" draggable="false" />
						</div>

						<div
							className="absolute -right-27 -bottom-37 w-65 md:right-[unset] md:-bottom-88 md:-left-35 md:w-115 md:max-w-1/2 md:-translate-x-100 xl:-bottom-55 xl:-left-8"
							ref={unfilteredStarRef}>
							<ReactSVG
								src={star}
								className="[&_svg]:fill-red md:-scale-x-100 [&_svg]:h-auto [&_svg]:w-full [&_svg]:rotate-12 [&_svg]:md:-rotate-6"
							/>
							<p className="absolute top-[43%] left-1/3 font-serif text-lg text-white md:right-1/3 md:left-[unset] md:text-2xl lg:text-3xl">
								VOL 5.2%
							</p>
						</div>
					</div>
				</div>
			</section>
		</>
	);
};

export default Products;
